import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { CircleFlag } from "../../common/CircleFlag";
import { useFixtures } from "../../common/contexts/fixturesContext";
import { getUserBets } from "../../services/betService";
import { Bet } from "../../types/Bet";
import { Match } from "../../types/Match";
import { getMatchKickoff } from "../../utils/betting";
import countryDict from "../../utils/countryDict";
import { getDateFromDate } from "../../utils/timeUtils";
import BetOutcomeIcon from "../components/BetOutcomeIcon";
import "./MobileRanking.scss";

interface UserBetsModalProps {
  open: boolean;
  onClose: () => void;
  userId: string | null;
  username: string;
}

interface SettledBet {
  bet: Bet;
  match: Match;
}

const stageLabel = (match: Match): string =>
  match.matchType.type === "Group"
    ? `Group ${match.matchType.value}`
    : match.matchType.value;

const UserBetsModal: React.FC<UserBetsModalProps> = ({
  open,
  onClose,
  userId,
  username,
}) => {
  const { matches } = useFixtures();
  const [loading, setLoading] = useState(false);
  const [settledBets, setSettledBets] = useState<SettledBet[]>([]);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setLoading(true);
    getUserBets(userId)
      .then((bets) => {
        if (cancelled) return;
        const matchById = new Map(matches.map((match) => [match.id, match]));
        const rows = bets
          .map((bet) => ({ bet, match: matchById.get(bet.matchId) }))
          // Only bets whose match has already been played (has a result).
          .filter((row): row is SettledBet => !!row.match && row.match.wasPlayed)
          .sort(
            (a, b) =>
              (getMatchKickoff(b.match)?.getTime() ?? 0) -
              (getMatchKickoff(a.match)?.getTime() ?? 0)
          );
        setSettledBets(rows);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId, matches]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle className="userBets__title">
        {username}'s results
        <IconButton onClick={onClose} aria-label="Close" edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box className="userBets__loading">
            <CircularProgress />
          </Box>
        ) : settledBets.length === 0 ? (
          <Typography className="userBets__empty">
            No played matches yet.
          </Typography>
        ) : (
          settledBets.map(({ bet, match }) => (
            <Box className="userBets__row" key={`user-bet-${bet.id}`}>
              <Box className="userBets__meta">
                <span>{stageLabel(match)}</span>
                <span>{getDateFromDate(match.kickOffTime?.dateTime || "")}</span>
              </Box>
              <Box className="userBets__teams">
                <span className="userBets__name userBets__name--home">
                  {match.homeTeam.internationalName}
                </span>
                <CircleFlag
                  countryCode={
                    countryDict.get(match.homeTeam.internationalName) || "pl"
                  }
                  height={22}
                />
                <Box className="userBets__scores">
                  <span className="userBets__betScore">
                    {bet.homeTeamScoreBet}–{bet.awayTeamScoreBet}
                  </span>
                  <span className="userBets__resultScore">
                    ({match.homeScoreRegular}–{match.awayScoreRegular})
                  </span>
                </Box>
                <CircleFlag
                  countryCode={
                    countryDict.get(match.awayTeam.internationalName) || "pl"
                  }
                  height={22}
                />
                <span className="userBets__name">
                  {match.awayTeam.internationalName}
                </span>
              </Box>
              <Box className="userBets__outcome">
                <BetOutcomeIcon outcome={bet.betResult} />
                <span>{bet.points} pts</span>
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserBetsModal;
